<script setup>
import { reactive, computed, watch, ref, nextTick } from 'vue'

import { useConnectionStore } from '@/stores/useConnectionStore'
import { useCardStore } from '@/stores/useCardStore'
import { useUserStore } from '@/stores/useUserStore'
import { useSpaceStore } from '@/stores/useSpaceStore'
import { useGlobalStore } from '@/stores/useGlobalStore'
import { useThemeStore } from '@/stores/useThemeStore'
import { useStoreAction } from '@/composables/useStoreAction.js'

import utils from '@/utils.js'
import last from 'lodash-es/last'

const globalStore = useGlobalStore()
const connectionStore = useConnectionStore()
const cardStore = useCardStore()
const userStore = useUserStore()
const spaceStore = useSpaceStore()
const themeStore = useThemeStore()

useStoreAction(globalStore, {
  triggerUpdateCurrentConnectorColor: () => updateCurrentConnectorColor(),
  triggerClearCurrentConnectorColor: () => clearCurrentConnectorColor()
})

const emit = defineEmits(['shouldRenderParent'])

const props = defineProps({
  card: Object,
  box: Object,
  list: Object,
  isConnectingTo: Boolean,
  isConnectingFrom: Boolean,
  isRemoteConnecting: Boolean,
  remoteConnectionColor: String,
  isVisibleInViewport: Boolean,
  defaultBackgroundColor: String,
  currentBackgroundColor: String,
  parentDetailsIsVisible: Boolean,
  backgroundIsTransparent: Boolean
})

const state = reactive({
  currentConnectorColor: ''
})

const item = computed(() => props.card || props.box || props.list)
// excludes cards in selected lists, because the selected list is connected to instead
const multipleItemsSelectedIds = computed(() => {
  const listIds = globalStore.multipleListsSelectedIds
  const cardIds = globalStore.multipleCardsSelectedIds.filter(cardId => {
    const card = cardStore.getCard(cardId)
    return !listIds.includes(card?.listId)
  })
  return cardIds.concat(globalStore.multipleBoxesSelectedIds, listIds)
})
const itemIsSelected = computed(() => {
  return multipleItemsSelectedIds.value.includes(item.value.id)
})

// space

const canEditSpace = computed(() => userStore.getUserCanEditSpace)
const backgroundColorIsDark = computed(() => {
  const color = props.currentBackgroundColor || props.defaultBackgroundColor
  return utils.colorIsDark(color)
})
const canEditItem = computed(() => {
  if (userStore.getUserIsCommentOnly) {
    return userStore.getItemIsCreatedByUser(item.value)
  }
  return canEditSpace.value
})
const isPresentationMode = computed(() => globalStore.isPresentationMode)
const isDisabled = computed(() => {
  if (item.value.isLocked) { return true }
  return !canEditItem.value
})
const connectorClasses = computed(() => {
  return {
    'is-disabled': isDisabled.value,
    'has-connections': hasConnections.value,
    'is-presentation-mode': isPresentationMode.value,
    'is-connecting': props.isConnectingTo || props.isConnectingFrom
  }
})

// connections

const connectedConnections = computed(() => connectionStore.getConnectionsByItemId(item.value.id))
const itemConnections = computed(() => connectionStore.getConnectionsByItemId(item.value.id))
const currentConnectionColor = computed(() => globalStore.currentConnectionColor)
const connectionFromAnotherItemConnectedToCurrentItem = (anotherItemId) => {
  return itemConnections.value.find(connection => {
    const isConnectedToStart = connection.startItemId === anotherItemId
    const isConnectedToEnd = connection.endItemId === anotherItemId
    return isConnectedToStart || isConnectedToEnd
  })
}
const connectionsFromMultipleItemsConnectedToCurrentItem = (otherItemIds) => {
  let currentItemConnection
  otherItemIds.find(anotherItemId => {
    return itemConnections.value.find(connection => {
      const isConnectedToStart = connection.startItemId === anotherItemId
      const isConnectedToEnd = connection.endItemId === anotherItemId
      if (isConnectedToStart || isConnectedToEnd) {
        currentItemConnection = connection
        return true
      }
    })
  })
  return currentItemConnection
}
const connectionColorisDark = computed(() => {
  const connection = last(connectedConnections.value)
  if (!connection) { return }
  return utils.colorIsDark(connection.color)
})
const hasConnections = computed(() => {
  const connections = itemConnections.value
  return Boolean(connections.length)
})
const createCurrentConnection = (event) => {
  const cursor = utils.cursorPositionInViewport(event)
  let itemIds = [item.value.id]
  if (multipleItemsSelectedIds.value.length) {
    itemIds = multipleItemsSelectedIds.value
  }
  globalStore.currentConnectionStartItemIds = itemIds
  globalStore.currentConnectionCursorStart = cursor
}

// connector button

const isConnectorBackgroundColorLight = computed(() => {
  if (connectionColorisDark.value) { return !connectionColorisDark.value }
  return !backgroundColorIsDark.value
})
const isImageCard = computed(() => {
  if (!props.card) { return }
  const name = props.card.name
  const urls = utils.urlsFromString(name) || []
  let isImage
  urls.forEach(url => {
    if (utils.urlIsImage(url)) {
      isImage = true
    } else if (utils.urlIsVideo(url)) {
      isImage = true
    }
  })
  return isImage
})
const connectorButtonBackground = computed(() => {
  if (globalStore.currentUserIsDraggingCard) { return }
  if (hasConnections.value || props.isConnectingFrom || props.isConnectingTo) { return }
  if (props.backgroundIsTransparent) { return }
  if (state.currentConnectorColor) { return state.currentConnectorColor }
  if (isImageCard.value) {
    return 'transparent'
  }
  return props.currentBackgroundColor
})
const buttonStyles = computed(() => {
  const styles = { background: connectorButtonBackground.value }
  return styles
})
const connectorGlowStyle = computed(() => {
  if (!props.isVisibleInViewport) { return }
  if (globalStore.currentUserIsDraggingList) { return }
  if (globalStore.getIsInteractingWithConnection) { return }
  if (!utils.arrayHasItems(connectedConnections.value) && !globalStore.currentUserIsDrawingConnection) { return } // cards with no connections
  const color = connectedToAnotherItemDetailsVisibleColor.value ||
    connectedToAnotherItemBeingDraggedColor.value ||
    connectedToConnectionDetailsIsVisibleColor.value ||
    currentUserIsHoveringOverConnectorColor.value ||
    currentUserIsHoveringOverConnectionColor.value ||
    currentUserIsMultipleSelectedConnectionColor.value ||
    currentUserIsHoveringOverConnectedItemColor.value ||
    currentUserIsMultipleSelectedItemColor.value ||
    currentUserIsCreatingConnectionColor.value
  return { background: color }
})
const connectionColor = (connection) => {
  if (!connection) { return }
  return connection?.color
}
// another item that is connected to this one is being edited
const connectedToAnotherItemDetailsVisibleColor = computed(() => {
  if (props.parentDetailsIsVisible) { return }
  const anotherItemId = globalStore.cardDetailsIsVisibleForCardId || globalStore.boxDetailsIsVisibleForBoxId || globalStore.listDetailsIsVisibleForListId
  if (!anotherItemId) { return }
  const connection = connectionFromAnotherItemConnectedToCurrentItem(anotherItemId)
  return connectionColor(connection)
})
// another item that is connected to this one is being dragged
const connectedToAnotherItemBeingDraggedColor = computed(() => {
  const isDraggingCard = globalStore.currentUserIsDraggingCard
  const isDraggingBox = globalStore.currentUserIsDraggingBox
  const isDraggingList = globalStore.currentUserIsDraggingList
  if (!isDraggingCard && !isDraggingBox && !isDraggingList) { return }
  const itemId = globalStore.currentDraggingCardId || globalStore.currentDraggingBoxId || globalStore.currentDraggingListId
  const connection = connectionFromAnotherItemConnectedToCurrentItem(itemId)
  const color = connectionColor(connection)
  if (color) {
    emit('shouldRenderParent', true)
  } else {
    emit('shouldRenderParent', false)
  }
  return color
})
// a connection that is connected to this item is being edited
const connectedToConnectionDetailsIsVisibleColor = computed(() => {
  const connectionDetailsVisibleId = globalStore.connectionDetailsIsVisibleForConnectionId
  if (!connectionDetailsVisibleId) { return }
  const connection = itemConnections.value.find(connection => connection.id === connectionDetailsVisibleId)
  return connection?.color
})
// a connection that is connected to this item connector that is being hovered over
const currentUserIsHoveringOverConnectorColor = computed(() => {
  const itemId = globalStore.currentUserIsHoveringOverConnectorItemId

  const connection = itemConnections.value.find(connection => {
    const isStart = connection.startItemId === itemId
    const isEnd = connection.endItemId === itemId
    return isStart || isEnd
  })
  return connectionColor(connection)
})
// a connection that is connected to this item is being hovered over
const currentUserIsHoveringOverConnectionColor = computed(() => {
  const connectionId = globalStore.currentUserIsHoveringOverConnectionId || globalStore.currentUserIsDraggingConnectionIdLabel
  const connection = itemConnections.value.find(currentItemConnection => currentItemConnection.id === connectionId)
  return connectionColor(connection)
})
// a connection that is connected to this item, is paint selected
const currentUserIsMultipleSelectedConnectionColor = computed(() => {
  const connectionIds = globalStore.multipleConnectionsSelectedIds
  const connection = itemConnections.value.find(currentItemConnection => connectionIds.includes(currentItemConnection.id))
  return connectionColor(connection)
})
// this item, or another item connected to this item, is being hovered over
const currentUserIsHoveringOverConnectedItemColor = computed(() => {
  const itemId = globalStore.currentUserIsHoveringOverCardId || globalStore.currentUserIsHoveringOverBoxId || globalStore.currentUserIsHoveringOverListId
  const connection = connectionFromAnotherItemConnectedToCurrentItem(itemId)
  return connectionColor(connection)
})
// the color of this item, or another item connected to this item, that is paint selected
const currentUserIsMultipleSelectedItemColor = computed(() => {
  const itemIds = globalStore.multipleCardsSelectedIds
  const connection = connectionsFromMultipleItemsConnectedToCurrentItem(itemIds)
  return connectionColor(connection)
})
// a current connection is being made by dragging from this item's connector
const currentUserIsCreatingConnectionColor = computed(() => {
  if (!globalStore.currentUserIsDrawingConnection) { return }
  const itemIds = globalStore.currentConnectionStartItemIds
  const isCurrentlyConnecting = itemIds.includes(item.value.id)
  if (!isCurrentlyConnecting) { return }
  return globalStore.currentConnectionColor
})

// connector preview color

const updateCurrentConnectionColor = () => {
  connectionStore.newCurrentConnectionColor = themeStore.randomColor()
  globalStore.currentConnectionColor = connectionStore.getNewConnectionColor
  globalStore.triggerUpdateCurrentConnectorColor()
  state.currentConnectorColor = globalStore.currentConnectionColor
}
const updateCurrentConnectorColor = () => {
  if (!itemIsSelected.value) { return }
  state.currentConnectorColor = globalStore.currentConnectionColor
}
const clearCurrentConnectorColor = () => {
  state.currentConnectorColor = ''
}

// handle events

const startConnecting = (event) => {
  if (isDisabled.value) { return }
  if (utils.isMultiTouch(event)) { return }
  globalStore.closeAllDialogs()
  globalStore.preventDraggedCardFromShowingDetails = true
  globalStore.preventDraggedBoxFromShowingDetails = true
  globalStore.preventDraggedListFromShowingDetails = true
  if (!globalStore.currentUserIsDrawingConnection) {
    createCurrentConnection(event)
  }
  globalStore.updateCurrentUserIsDrawingConnection(true)
}
const handleMouseEnterConnector = (event) => {
  globalStore.currentUserIsHoveringOverConnectorItemId = item.value.id
  if (globalStore.currentUserIsDrawingConnection) { return }
  if (globalStore.getIsInteractingWithConnection) { return }
  updateCurrentConnectionColor()
}
const handleMouseLeaveConnector = () => {
  globalStore.currentUserIsHoveringOverConnectorItemId = ''
  globalStore.triggerClearCurrentConnectorColor()
  clearCurrentConnectorColor()
}

</script>

<template lang="pug">
.connector(
  :data-item-id="item.id"
  :key="item.id"
  @mousedown.left="startConnecting"
  @touchstart="startConnecting"
  @mouseenter="handleMouseEnterConnector"
  @mouseleave="handleMouseLeaveConnector"
  :class="connectorClasses"
)
  .connector-glow(:style="connectorGlowStyle" tabindex="-1")
  .connected-colors
    template(v-if="props.isConnectingTo || props.isConnectingFrom")
      .color(:style="{ background: currentConnectionColor}")
    template(v-else-if="state.currentConnectorColor")
      .color(:style="{ background: state.currentConnectorColor }")
    template(v-else-if="props.isRemoteConnecting")
      .color(:style="{ background: props.remoteConnectionColor }")
    template(v-else v-for="connection in connectedConnections")
      .color(:style="{ background: connection?.color}")

  button.inline-button.connector-button(
    :class="{ active: props.isConnectingTo || props.isConnectingFrom, 'is-light': isConnectorBackgroundColorLight, 'is-dark': !isConnectorBackgroundColorLight }"
    :style="buttonStyles"
    tabindex="-1"
  )
    template(v-if="hasConnections || props.isConnectingFrom || props.isConnectingTo")
      img.connector-icon(src="@/assets/connector-closed-in-card.svg" :class="{ 'is-light': !connectionColorisDark, 'is-dark': connectionColorisDark }")

</template>

<style lang="stylus">
.connector
  position relative
  z-index 2
  padding 8px
  align-self right
  cursor cell
  button
    z-index 1
  &.is-disabled
    opacity 0.5
    pointer-events none
    cursor default
    &.has-connections
      opacity 1
  &.is-presentation-mode
    opacity 0.5
    &:hover,
    &.is-connecting,
    &.has-connections
      opacity 1

  // &.invisible
  //   height 32px
  //   width 36px
  //   padding 0
  //   position absolute
  //   left 0
  //   top 0
  //   background transparent
  //   pointer-events none
  //   button
  //     width 0
  //     height 0
  //     min-width 0
  //     padding 0
  //     border none
  //     border-color var(--primary-border)

  .connector-glow
    position absolute
    width 32px
    height 32px
    top 0px
    left -1px
    border-radius 100px
    pointer-events none
  .connector-button
    background-color transparent
    border-radius 100px
    width 14px
    height 14px
    padding 0
    border 1px solid var(--primary-border)

  .connector-icon
    position absolute
    left -1px
    top -1px
    width 9.5px
    user-drag none
    -webkit-user-drag none
    pointer-events none
    &.is-light
      filter none
    &.is-dark
      filter invert()

  button
    &.is-light
      border-color var(--primary-border-on-light-background)
    &.is-dark
      border-color var(--primary-border-on-dark-background)

  .connected-colors
    position absolute
    display flex
    height 12px
    width 12px
    top 10px
    left 9px
    overflow hidden
    border-radius 100px
    .color
      width 100%

</style>
